import { useEffect, useState } from 'react';
import type { DischargePreview } from '@ehr/shared';
import { api } from '../lib/api.js';
import { dischargeConfirmLines } from '../lib/dischargeNote.js';
import { Modal } from './Modal.js';

/**
 * ehr#708 (Jay 10-30, Q4): before a discharge summary is signed off, sent to the supervisor or
 * co-signed, this lists exactly what the EHR will do once it is final: set the client to
 * Discharged, close their current treatment plan, cancel their future appointments (each named;
 * listed for staff to cancel in the Calendar until the Calendar can do it).
 * GET /contacts/:id/discharge-preview (?note=<id> for a saved note). Go back changes nothing.
 */
export function DischargeConfirm({
  contactId,
  noteId,
  okLabel,
  busy = false,
  onOk,
  onClose,
}: {
  contactId: string;
  /** The saved note (null: not saved yet, the caller's own new one). */
  noteId: string | null;
  /** The green button: Sign Off Note, Submit to Supervisor, Co-sign and finalize. */
  okLabel: string;
  busy?: boolean;
  onOk: () => void;
  onClose: () => void;
}) {
  const [preview, setPreview] = useState<DischargePreview | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    api
      .get<DischargePreview>(
        `/contacts/${contactId}/discharge-preview${noteId ? `?note=${encodeURIComponent(noteId)}` : ''}`,
      )
      .then((p) => live && setPreview(p))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [contactId, noteId]);
  return (
    <Modal
      title="Discharge this client?"
      onClose={onClose}
      className="pn-discharge-ask"
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Go back
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={busy || (!preview && !failed)}
            onClick={onOk}
          >
            {okLabel}
          </button>
        </>
      }
    >
      <DischargeConfirmBody preview={preview} failed={failed} />
    </Modal>
  );
}

/** The confirm's words (its own part, so a test can read them without the network). */
export function DischargeConfirmBody({
  preview,
  failed,
}: {
  preview: DischargePreview | null;
  failed: boolean;
}) {
  if (failed)
    return (
      <p className="pn-modal-p" data-test="discharge-ask">
        Signing this discharge summary sets the client to Discharged and closes their current
        treatment plan. Afterwards it says what happened to their future appointments. The details
        could not be loaded.
      </p>
    );
  if (!preview)
    return (
      <p className="pn-modal-p muted" data-test="discharge-ask">
        Checking what this will change…
      </p>
    );
  const c = dischargeConfirmLines(preview);
  return (
    <div data-test="discharge-ask">
      <p className="pn-modal-p">{c.intro}</p>
      <ul className="pn-discharge-list">
        {c.lines.map((l, i) => (
          <li key={l}>
            {l}
            {i === c.lines.length - 1 && c.appointments.length > 0 && (
              <ul data-test="discharge-appts">
                {c.appointments.map((a, j) => (
                  <li key={`${a}:${j}`}>{a}</li>
                ))}
                {c.more > 0 && <li>and {c.more} more</li>}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
